import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { buildCampaign, buildFightStages, campaignStorageKey, combatProfile, fightControls, finishingMoveNames, japaneseCalls } from '../data/fightingGame';
import { audioAsset, attractAudio } from '../lib/attractAudio';
import { nextVoiceSpeed, useAudioPreferences } from '../lib/audioPreferences';
import type { CharacterProfile } from '../types';
import { IdleFighterSprite } from './IdleFighterSprite';

interface FightingGameProps {
  characters: CharacterProfile[];
  mode: '1p' | '2p';
  onPlayShooter: () => void;
  onExit: () => void;
}

interface FighterState {
  x: number;
  y: number;
  velocityY: number;
  health: number;
  blocking: boolean;
  crouching: boolean;
  hit: number;
  attack: 'punch' | 'uppercut' | 'kick' | 'special' | null;
}

interface BattleState {
  p1: FighterState;
  p2: FighterState;
  projectiles: ProjectileState[];
}

interface ProjectileState {
  id: number;
  owner: 1 | 2;
  x: number;
  direction: 1 | -1;
  speed: number;
  damage: number;
  knockback: number;
  hitStun: number;
  scale: number;
}

interface DirectionInput {
  direction: 'down' | 'left' | 'right';
  at: number;
}

interface RoundWins {
  p1: number;
  p2: number;
}

interface ComboState {
  owner: 1 | 2;
  count: number;
}

const freshFighter = (x: number): FighterState => ({ x, y: 0, velocityY: 0, health: 100, blocking: false, crouching: false, hit: 0, attack: null });
const freshBattle = (): BattleState => ({ p1: freshFighter(25), p2: freshFighter(75), projectiles: [] });
const VANDAL_GIF = '/assets/characters/vandal/idle/previews/vandal_idle_preview.gif?v=20260717-5';

const savedCampaignIndex = (fighterId: string, length: number) => {
  try {
    const value = Number(window.localStorage.getItem(campaignStorageKey(fighterId)) ?? 0);
    return Number.isFinite(value) ? Math.max(0, Math.min(length - 1, Math.floor(value))) : 0;
  } catch {
    return 0;
  }
};

export function FightingGame({ characters, mode, onPlayShooter, onExit }: FightingGameProps) {
  const roster = useMemo(() => characters.filter((character) => character.id !== 'the-convergence').slice(0, 30), [characters]);
  const stages = useMemo(() => buildFightStages(characters), [characters]);
  const debugFightResult = useMemo(() => import.meta.env.DEV && new URLSearchParams(window.location.search).get('fightResult') === '1', []);
  const debugCampaignFight = useMemo(() => import.meta.env.DEV ? Number(new URLSearchParams(window.location.search).get('fightCampaign') ?? 0) : 0, []);
  const debugAutoMatch = useMemo(() => import.meta.env.DEV ? new URLSearchParams(window.location.search).get('fightAutoMatch') : null, []);
  const [screen, setScreen] = useState<'select' | 'fight' | 'finisher' | 'result'>(debugFightResult ? 'result' : 'select');
  const [selectionTurn, setSelectionTurn] = useState<1 | 2>(1);
  const [cursor, setCursor] = useState(0);
  const [p1Index, setP1Index] = useState<number | null>(null);
  const [p2Index, setP2Index] = useState<number | null>(null);
  const [battle, setBattle] = useState(freshBattle);
  const [matchNumber, setMatchNumber] = useState(1);
  const [roundNumber, setRoundNumber] = useState(1);
  const [roundWins, setRoundWins] = useState<RoundWins>({ p1: 0, p2: 0 });
  const [combo, setCombo] = useState<ComboState | null>(null);
  const [campaignIndex, setCampaignIndex] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [announcement, setAnnouncement] = useState('CHOOSE YOUR FIGHTER');
  const [showControls, setShowControls] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [vandalVisible, setVandalVisible] = useState(false);
  const [finisherIndex, setFinisherIndex] = useState(0);
  const [finisherStyle, setFinisherStyle] = useState(0);
  const [finisherSeed, setFinisherSeed] = useState(0);
  const [gibCount, setGibCount] = useState(42);
  const [resultWinner, setResultWinner] = useState<1 | 2>(1);
  const [gibBurst, setGibBurst] = useState(0);
  const [audioPreferences, updateAudioPreferences] = useAudioPreferences();
  const keys = useRef(new Set<string>());
  const debugStarted = useRef(false);
  const attackLock = useRef({ p1: 0, p2: 0 });
  const roundResolving = useRef(false);
  const comboRuntime = useRef({ owner: 1 as 1 | 2, count: 0, lastHitAt: 0, timer: 0 });
  const battleRef = useRef(battle);
  const showSettingsRef = useRef(showSettings);
  const projectileSequence = useRef(0);
  const inputHistory = useRef<{ p1: DirectionInput[]; p2: DirectionInput[] }>({ p1: [], p2: [] });
  const p1 = p1Index === null ? roster[cursor] : roster[p1Index];
  const campaign = useMemo(() => p1Index === null ? [] : buildCampaign(roster, roster[p1Index].id), [p1Index, roster]);
  const encounter = mode === '1p' ? campaign[campaignIndex] : undefined;
  const p2 = encounter
    ? encounter.kind === 'dark-self' ? p1 : characters.find((character) => character.id === encounter.opponentId) ?? roster[0]
    : p2Index === null ? roster[(cursor + 7) % Math.max(1, roster.length)] : roster[p2Index];
  const stage = encounter
    ? stages.find((candidate) => candidate.ownerId === encounter.stageOwnerId) ?? stages[0]
    : stages[stageIndex % Math.max(1, stages.length)];
  const p1Combat = useMemo(() => combatProfile(p1), [p1]);
  const p2Combat = useMemo(() => combatProfile(p2), [p2]);
  const campaignComplete = mode === '1p' && Boolean(encounter) && campaignIndex === campaign.length - 1 && resultWinner === 1 && screen === 'result';

  useEffect(() => {
    battleRef.current = battle;
  }, [battle]);

  useEffect(() => {
    showSettingsRef.current = showSettings;
  }, [showSettings]);

  const recordDirection = useCallback((player: 1 | 2, direction: DirectionInput['direction']) => {
    const key = player === 1 ? 'p1' : 'p2';
    const now = performance.now();
    const recent = inputHistory.current[key].filter((entry) => now - entry.at <= 700);
    if (recent[recent.length - 1]?.direction !== direction) recent.push({ direction, at: now });
    inputHistory.current[key] = recent.slice(-5);
  }, []);

  const consumeSpecialMotion = useCallback((player: 1 | 2) => {
    const key = player === 1 ? 'p1' : 'p2';
    const now = performance.now();
    const snapshot = battleRef.current;
    const fighter = player === 1 ? snapshot.p1 : snapshot.p2;
    const opponent = player === 1 ? snapshot.p2 : snapshot.p1;
    const toward = fighter.x <= opponent.x ? 'right' : 'left';
    const recent = inputHistory.current[key].filter((entry) => now - entry.at <= 620);
    const towardIndex = recent.map((entry) => entry.direction).lastIndexOf(toward);
    const completed = towardIndex > 0 && recent.slice(0, towardIndex).some((entry) => entry.direction === 'down');
    if (completed) inputHistory.current[key] = [];
    return completed;
  }, []);

  useEffect(() => {
    inputHistory.current = { p1: [], p2: [] };
  }, [roundNumber, screen]);

  useEffect(() => {
    attractAudio.setMusicVolume(audioPreferences.fightMusicVolume);
    attractAudio.setMusic(audioAsset('/assets/audio/music/island-circuit-loop.wav'));
    return () => {
      attractAudio.setMusicVolume(1);
      attractAudio.setMusic(null);
    };
  }, [audioPreferences.fightMusicVolume]);

  const playFightVoice = useCallback((character: CharacterProfile, clip: string) => {
    attractAudio.playIfUnlocked(audioAsset(`/assets/audio/fighting/${character.id}/${clip}.wav`), { kind: 'fight-voice', gain: 0.92 });
  }, []);
  const playEffect = useCallback((clip: string, rate = 1) => {
    const files: Record<string, string> = { 'boss-blast': 'vaporize', 'hit-confirm': 'target-hit' };
    attractAudio.playIfUnlocked(audioAsset(`/assets/audio/finale-game/${files[clip] ?? clip}.wav`), { kind: 'effect', gain: audioPreferences.fightEffectsVolume, playbackRate: rate });
  }, [audioPreferences.fightEffectsVolume]);

  const clearCombo = useCallback(() => {
    if (comboRuntime.current.timer) window.clearTimeout(comboRuntime.current.timer);
    comboRuntime.current = { owner: 1, count: 0, lastHitAt: 0, timer: 0 };
    setCombo(null);
  }, []);

  const registerComboHit = useCallback((owner: 1 | 2) => {
    const now = performance.now();
    const previous = comboRuntime.current;
    const count = previous.owner === owner && now - previous.lastHitAt < 920 ? previous.count + 1 : 1;
    if (previous.timer) window.clearTimeout(previous.timer);
    const timer = window.setTimeout(() => {
      comboRuntime.current.count = 0;
      setCombo(null);
    }, 1050);
    comboRuntime.current = { owner, count, lastHitAt: now, timer };
    setCombo(count >= 2 ? { owner, count } : null);
  }, []);

  useEffect(() => () => {
    if (comboRuntime.current.timer) window.clearTimeout(comboRuntime.current.timer);
  }, []);

  const scheduleVandal = useCallback((fightNumber: number) => {
    if (fightNumber % 2 !== 0 && Math.random() >= 0.5) return;
    window.setTimeout(() => {
      setVandalVisible(true);
      playFightVoice(roster.find((character) => character.id === 'vandal') ?? roster[0], 'cameo');
      window.setTimeout(() => setVandalVisible(false), 2300);
    }, 3200);
  }, [playFightVoice, roster]);

  const startMatch = useCallback((leftIndex: number, rightIndex: number) => {
    setP1Index(leftIndex);
    setP2Index(rightIndex);
    setStageIndex((leftIndex + matchNumber - 1) % Math.max(1, stages.length));
    setBattle(freshBattle());
    setRoundNumber(1);
    setRoundWins({ p1: 0, p2: 0 });
    clearCombo();
    roundResolving.current = false;
    const roundCall = 'ROUND 1 // FIGHT!';
    setAnnouncement(roundCall);
    setScreen('fight');
    setGibBurst(0);
    setResultWinner(1);
    window.setTimeout(() => setAnnouncement((current) => current === roundCall ? '' : current), 1500);
    scheduleVandal(matchNumber);
  }, [clearCombo, matchNumber, scheduleVandal, stages.length]);

  const startCampaignFight = useCallback((leftIndex: number, fightIndex: number) => {
    const fighter = roster[leftIndex];
    const ladder = buildCampaign(roster, fighter.id);
    const safeIndex = Math.max(0, Math.min(ladder.length - 1, fightIndex));
    const nextEncounter = ladder[safeIndex];
    setP1Index(leftIndex);
    setP2Index(null);
    setCampaignIndex(safeIndex);
    setMatchNumber(safeIndex + 1);
    setStageIndex(Math.max(0, stages.findIndex((candidate) => candidate.ownerId === nextEncounter.stageOwnerId)));
    setBattle(freshBattle());
    setRoundNumber(1);
    setRoundWins({ p1: 0, p2: 0 });
    clearCombo();
    roundResolving.current = false;
    const roundCall = nextEncounter.kind === 'rage-convergence' ? 'ROUND 1 // DOUBLE SPEED!' : 'ROUND 1 // FIGHT!';
    setAnnouncement(roundCall);
    setScreen('fight');
    setGibBurst(0);
    setResultWinner(1);
    window.setTimeout(() => setAnnouncement((current) => current === roundCall ? '' : current), 1500);
    scheduleVandal(safeIndex + 1);
  }, [clearCombo, roster, scheduleVandal, stages]);

  useEffect(() => {
    if (!debugCampaignFight || debugStarted.current || !roster.length) return;
    debugStarted.current = true;
    startCampaignFight(0, debugCampaignFight - 1);
  }, [debugCampaignFight, roster.length, startCampaignFight]);

  const choose = useCallback((index: number) => {
    playEffect('weapon-equip', 0.92 + index % 5 * 0.03);
    if (selectionTurn === 1) {
      setP1Index(index);
      if (mode === '2p') {
        setSelectionTurn(2);
        setCursor((index + 7) % roster.length);
        setAnnouncement('PLAYER 2 // CHOOSE YOUR FIGHTER');
      } else {
        const ladderLength = buildCampaign(roster, roster[index].id).length;
        const resumeAt = savedCampaignIndex(roster[index].id, ladderLength);
        setAnnouncement(resumeAt > 0 ? `CAMPAIGN RESUMED // FIGHT ${resumeAt + 1}` : 'ENTER THE ISLAND BRACKET');
        window.setTimeout(() => startCampaignFight(index, resumeAt), 650);
      }
      return;
    }
    setP2Index(index);
    window.setTimeout(() => startMatch(p1Index ?? 0, index), 650);
  }, [mode, p1Index, playEffect, roster, selectionTurn, startCampaignFight, startMatch]);

  const resolveRound = useCallback((winner: 1 | 2) => {
    if (roundResolving.current || screen !== 'fight') return;
    roundResolving.current = true;
    clearCombo();
    const winKey = winner === 1 ? 'p1' : 'p2';
    const nextWins = roundWins[winKey] + 1;
    setRoundWins((current) => ({ ...current, [winKey]: nextWins }));
    setResultWinner(winner);
    const winnerCharacter = winner === 1 ? p1 : p2;
    if (nextWins >= 2) {
      if (winner === 1 || mode === '2p') {
        setAnnouncement('LAY IT INTO THEM!');
        setScreen('finisher');
        roundResolving.current = false;
      } else {
        setAnnouncement(`${winnerCharacter.name.toUpperCase()} WINS`);
        window.setTimeout(() => setScreen('result'), 820);
      }
      return;
    }
    setAnnouncement(`${winnerCharacter.name.toUpperCase()} WINS ROUND ${roundNumber}`);
    playEffect('weapon-equip', 0.7);
    const nextRound = roundNumber + 1;
    window.setTimeout(() => {
      setBattle(freshBattle());
      setRoundNumber(nextRound);
      const roundCall = `ROUND ${nextRound} // FIGHT!`;
      setAnnouncement(roundCall);
      roundResolving.current = false;
      window.setTimeout(() => setAnnouncement((current) => current === roundCall ? '' : current), 1250);
    }, 1350);
  }, [clearCombo, mode, p1, p2, playEffect, roundNumber, roundWins, screen]);

  useEffect(() => {
    if (!debugAutoMatch || screen !== 'fight' || roundResolving.current) return;
    const owner = debugAutoMatch === 'p2' ? 2 : 1;
    const timer = window.setTimeout(() => {
      setBattle((current) => ({ ...current, [owner === 1 ? 'p2' : 'p1']: { ...(owner === 1 ? current.p2 : current.p1), health: 0, hit: 10 } }));
      resolveRound(owner);
    }, 650);
    return () => window.clearTimeout(timer);
  }, [debugAutoMatch, resolveRound, roundNumber, roundWins, screen]);

  const strike = useCallback((attacker: 1 | 2, kind: 'punch' | 'uppercut' | 'kick' | 'special') => {
    const battleSnapshot = battleRef.current;
    if (screen === 'finisher') {
      if (attacker !== resultWinner) return;
      const attackerState = attacker === 1 ? battleSnapshot.p1 : battleSnapshot.p2;
      const loserState = attacker === 1 ? battleSnapshot.p2 : battleSnapshot.p1;
      const crouchKey = attacker === 1 ? keys.current.has('KeyS') : keys.current.has('ArrowDown');
      if (kind === 'uppercut' && crouchKey && Math.abs(attackerState.x - loserState.x) < 16) {
        const victor = attacker === 1 ? p1 : p2;
        const victorCombat = attacker === 1 ? p1Combat : p2Combat;
        const name = finishingMoveNames(victor)[finisherIndex % 3];
        setFinisherStyle((finisherIndex + victorCombat.animationSeed) % 3);
        setFinisherSeed(victorCombat.animationSeed % 8);
        setGibCount(40 + victorCombat.animationSeed % 31);
        setFinisherIndex((current) => (current + 1) % 3);
        setResultWinner(attacker);
        setAnnouncement(name);
        setGibBurst((current) => current + 1);
        playFightVoice(victor, `finisher-${finisherIndex % 3 + 1}`);
        playEffect('boss-blast', 0.72);
        window.setTimeout(() => setScreen('result'), 2200);
      }
      return;
    }
    if (screen !== 'fight' || roundResolving.current || !p1 || !p2) return;
    const now = performance.now();
    const lockKey = attacker === 1 ? 'p1' : 'p2';
    if (now < attackLock.current[lockKey]) return;
    const combatSpeed = encounter?.speedMultiplier ?? 1;
    const attackerCombat = attacker === 1 ? p1Combat : p2Combat;
    attackLock.current[lockKey] = now + (kind === 'special' ? attackerCombat.specialCooldownMs : 330) / combatSpeed;
    const attackerState = attacker === 1 ? battleSnapshot.p1 : battleSnapshot.p2;
    const defenderState = attacker === 1 ? battleSnapshot.p2 : battleSnapshot.p1;
    const attackerCharacter = attacker === 1 ? p1 : p2;
    if (attackerState.hit > 0 || attackerState.blocking) return;
    if (kind === 'special') {
      if (battleSnapshot.projectiles.filter((projectile) => projectile.owner === attacker).length >= 2) return;
      playFightVoice(attackerCharacter, `special-${(matchNumber + attacker) % 3 + 1}`);
      const specialCall = japaneseCalls[(matchNumber + (attacker === 1 ? p1Index ?? 0 : p2Index ?? 0)) % japaneseCalls.length];
      setAnnouncement(specialCall);
      window.setTimeout(() => setAnnouncement((current) => current === specialCall ? '' : current), 650);
      setBattle((current) => {
        const attacking = attacker === 1 ? current.p1 : current.p2;
        const defending = attacker === 1 ? current.p2 : current.p1;
        const direction = attacking.x <= defending.x ? 1 : -1;
        const projectile: ProjectileState = {
          id: ++projectileSequence.current,
          owner: attacker,
          x: attacking.x + direction * 4,
          direction,
          speed: attackerCombat.specialSpeed,
          damage: attackerCombat.specialDamage,
          knockback: attackerCombat.specialKnockback,
          hitStun: attackerCombat.specialHitStun,
          scale: attackerCombat.specialScale,
        };
        return { ...current, projectiles: [...current.projectiles, projectile].slice(-6), [attacker === 1 ? 'p1' : 'p2']: { ...attacking, attack: kind } };
      });
      window.setTimeout(() => setBattle((current) => ({ ...current, [attacker === 1 ? 'p1' : 'p2']: { ...(attacker === 1 ? current.p1 : current.p2), attack: null } })), 520 / combatSpeed);
      return;
    }
    playFightVoice(attackerCharacter, `grunt-${(matchNumber + attacker + Math.round(now)) % 3 + 1}`);
    const distance = Math.abs(attackerState.x - defenderState.x);
    const damage = kind === 'uppercut' ? 14 : kind === 'kick' ? 10 : 7;
    setBattle((current) => {
      const attacking = attacker === 1 ? current.p1 : current.p2;
      const defending = attacker === 1 ? current.p2 : current.p1;
      const hit = distance < (kind === 'kick' ? 18 : 14);
      const riggedDefense = mode === '1p' && encounter?.forcedLoss && attacker === 1;
      const dealt = hit && !riggedDefense ? Math.max(1, defending.blocking ? Math.ceil(damage * 0.2) : damage) : 0;
      const direction = attacking.x <= defending.x ? 1 : -1;
      const knockback = dealt ? (kind === 'uppercut' ? 4.4 : kind === 'kick' ? 3.2 : 1.8) * direction : 0;
      const hitFrames = kind === 'uppercut' ? 11 : kind === 'kick' ? 8 : 6;
      const nextDefender = { ...defending, x: Math.max(4, Math.min(96, defending.x + knockback)), health: Math.max(0, defending.health - dealt), hit: dealt ? hitFrames : defending.hit, blocking: dealt ? false : defending.blocking };
      const next = { ...current, [attacker === 1 ? 'p1' : 'p2']: { ...attacking, attack: kind }, [attacker === 1 ? 'p2' : 'p1']: nextDefender } as BattleState;
      if (dealt) {
        registerComboHit(attacker);
        playEffect(kind === 'uppercut' ? 'boss-blast' : 'hit-confirm', kind === 'kick' ? 0.82 : 1);
        playFightVoice(attacker === 1 ? p2 : p1, `hit-${damage % 3 + 1}`);
        if (nextDefender.health === 0) window.setTimeout(() => resolveRound(attacker), 80);
      }
      return next;
    });
    window.setTimeout(() => setBattle((current) => ({ ...current, [attacker === 1 ? 'p1' : 'p2']: { ...(attacker === 1 ? current.p1 : current.p2), attack: null } })), 260);
  }, [encounter, finisherIndex, matchNumber, mode, p1, p1Combat, p1Index, p2, p2Combat, p2Index, playEffect, playFightVoice, registerComboHit, resolveRound, resultWinner, screen]);

  useEffect(() => {
    if (mode !== '1p' || screen !== 'fight' || !encounter?.forcedLoss) return;
    const riggedTimer = window.setTimeout(() => {
      setAnnouncement('PERFECTLY LEGAL INPUT!');
      playFightVoice(p2, 'special-3');
      playEffect('boss-blast', 0.58);
      setBattle((current) => ({ ...current, p1: { ...current.p1, health: 0, hit: 10 }, p2: { ...current.p2, health: 100 } }));
      setRoundWins({ p1: 0, p2: 2 });
      setResultWinner(2);
      window.setTimeout(() => setScreen('result'), 1050);
    }, 7600);
    return () => window.clearTimeout(riggedTimer);
  }, [encounter, mode, p2, playEffect, playFightVoice, screen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      keys.current.add(event.code);
      if (event.code === 'Escape') { event.preventDefault(); setShowSettings((current) => !current); return; }
      if (showSettings) return;
      if (screen === 'select') {
        if (['ArrowLeft', 'KeyA'].includes(event.code)) setCursor((current) => (current - 1 + roster.length) % roster.length);
        if (['ArrowRight', 'KeyD'].includes(event.code)) setCursor((current) => (current + 1) % roster.length);
        if (['ArrowUp', 'KeyW'].includes(event.code)) setCursor((current) => (current - 5 + roster.length) % roster.length);
        if (['ArrowDown', 'KeyS'].includes(event.code)) setCursor((current) => (current + 5) % roster.length);
        if (event.code === 'Enter' || event.code === 'Space') choose(cursor);
      }
      if ((screen === 'fight' || screen === 'finisher') && !event.repeat) {
        const directions: Partial<Record<string, [1 | 2, DirectionInput['direction']]>> = {
          KeyS: [1, 'down'], KeyA: [1, 'left'], KeyD: [1, 'right'],
          ArrowDown: [2, 'down'], ArrowLeft: [2, 'left'], ArrowRight: [2, 'right'],
        };
        const direction = directions[event.code];
        if (direction && (direction[0] === 1 || mode === '2p')) recordDirection(...direction);
      }
      if (event.code === 'KeyF') strike(1, consumeSpecialMotion(1) ? 'special' : 'punch');
      if (event.code === 'KeyG') strike(1, 'uppercut');
      if (event.code === 'KeyH') strike(1, 'kick');
      if (event.code === 'KeyQ') strike(1, 'special');
      if (mode === '2p') {
        if (event.code === 'KeyJ') strike(2, consumeSpecialMotion(2) ? 'special' : 'punch');
        if (event.code === 'KeyK') strike(2, 'uppercut');
        if (event.code === 'KeyL') strike(2, 'kick');
        if (event.code === 'KeyO') strike(2, 'special');
      }
    };
    const onKeyUp = (event: KeyboardEvent) => keys.current.delete(event.code);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
  }, [choose, consumeSpecialMotion, cursor, mode, recordDirection, roster.length, screen, showSettings, strike]);

  useEffect(() => {
    if (screen !== 'fight' || showSettings) return;
    let animation = 0;
    let previous = performance.now();
    const tick = (now: number) => {
      if (now - previous >= 32) {
        previous = now;
        setBattle((current) => {
          if (roundResolving.current) return current;
          const combatSpeed = encounter?.speedMultiplier ?? 1;
          const aggression = encounter?.aggression ?? Math.min(1.5, 0.8 + matchNumber * 0.025);
          const move = (fighter: FighterState, player: 1 | 2) => {
            const left = player === 1 ? keys.current.has('KeyA') || keys.current.has('Pad1Left') : keys.current.has('ArrowLeft') || keys.current.has('Pad2Left');
            const right = player === 1 ? keys.current.has('KeyD') || keys.current.has('Pad1Right') : keys.current.has('ArrowRight') || keys.current.has('Pad2Right');
            const jump = player === 1 ? keys.current.has('KeyW') || keys.current.has('Pad1Up') : keys.current.has('ArrowUp') || keys.current.has('Pad2Up');
            const crouch = player === 1 ? keys.current.has('KeyS') || keys.current.has('Pad1Down') : keys.current.has('ArrowDown') || keys.current.has('Pad2Down');
            const block = player === 1 ? keys.current.has('KeyR') || keys.current.has('Pad1Block') : keys.current.has('KeyU') || keys.current.has('Pad2Block');
            const run = player === 1 ? keys.current.has('KeyT') || keys.current.has('Pad1Run') : keys.current.has('KeyI') || keys.current.has('Pad2Run');
            let velocityY = fighter.velocityY;
            let y = fighter.y;
            if (fighter.hit > 0) {
              y = Math.max(0, y + velocityY);
              velocityY = y > 0 ? velocityY - 0.18 * combatSpeed : 0;
              return { ...fighter, y, velocityY, blocking: false, crouching: false, hit: fighter.hit - 1 };
            }
            let x = fighter.x + (right ? 1 : 0) * (run ? 1.35 : 0.72) * combatSpeed - (left ? 1 : 0) * (run ? 1.35 : 0.72) * combatSpeed;
            if (jump && y === 0) velocityY = 2.5 * Math.sqrt(combatSpeed);
            y = Math.max(0, y + velocityY);
            velocityY = y > 0 ? velocityY - 0.18 * combatSpeed : 0;
            return { ...fighter, x: Math.max(4, Math.min(96, x)), y, velocityY, crouching: crouch, blocking: block, hit: Math.max(0, fighter.hit - 1) };
          };
          let nextP1 = move(current.p1, 1);
          let nextP2 = mode === '2p' ? move(current.p2, 2) : current.p2.hit > 0
            ? { ...current.p2, blocking: false, crouching: false, hit: current.p2.hit - 1 }
            : { ...current.p2, x: current.p2.x + Math.sign(current.p1.x - current.p2.x) * (Math.abs(current.p1.x - current.p2.x) > 14 ? 0.36 * combatSpeed * aggression : 0), blocking: matchNumber > 4 && Math.random() < 0.028 * aggression, crouching: false };
          if (Math.abs(nextP1.x - nextP2.x) < 8) {
            const midpoint = (nextP1.x + nextP2.x) / 2;
            if (nextP1.x <= nextP2.x) {
              nextP1 = { ...nextP1, x: Math.max(4, midpoint - 4) };
              nextP2 = { ...nextP2, x: Math.min(96, midpoint + 4) };
            } else {
              nextP1 = { ...nextP1, x: Math.min(96, midpoint + 4) };
              nextP2 = { ...nextP2, x: Math.max(4, midpoint - 4) };
            }
          }
          let projectiles = current.projectiles
            .map((projectile) => ({ ...projectile, x: projectile.x + projectile.direction * projectile.speed * combatSpeed }))
            .filter((projectile) => projectile.x >= -4 && projectile.x <= 104);
          const removedProjectiles = new Set<number>();
          for (let leftIndex = 0; leftIndex < projectiles.length; leftIndex += 1) {
            const left = projectiles[leftIndex];
            if (removedProjectiles.has(left.id)) continue;
            for (let rightIndex = leftIndex + 1; rightIndex < projectiles.length; rightIndex += 1) {
              const right = projectiles[rightIndex];
              if (left.owner === right.owner || removedProjectiles.has(right.id)) continue;
              if (Math.abs(left.x - right.x) <= 3.4 + (left.scale + right.scale)) {
                removedProjectiles.add(left.id);
                removedProjectiles.add(right.id);
                playEffect('boss-blast', .42);
                break;
              }
            }
          }
          for (const projectile of projectiles) {
            if (removedProjectiles.has(projectile.id)) continue;
            const target = projectile.owner === 1 ? nextP2 : nextP1;
            if (Math.abs(projectile.x - target.x) >= 3.5 + projectile.scale * 1.4) continue;
            const riggedDefense = mode === '1p' && encounter?.forcedLoss && projectile.owner === 1;
            const dealt = riggedDefense ? 0 : target.blocking ? Math.max(1, Math.ceil(projectile.damage * .18)) : projectile.damage;
            const struck = {
              ...target,
              x: Math.max(4, Math.min(96, target.x + projectile.direction * (dealt ? projectile.knockback : 0))),
              health: Math.max(0, target.health - dealt),
              hit: dealt ? projectile.hitStun : target.hit,
              blocking: dealt ? false : target.blocking,
            };
            if (projectile.owner === 1) nextP2 = struck; else nextP1 = struck;
            removedProjectiles.add(projectile.id);
            if (dealt) {
              registerComboHit(projectile.owner);
              playEffect(projectile.damage >= 16 ? 'boss-blast' : 'hit-confirm', .62 + projectile.scale * .08);
              playFightVoice(projectile.owner === 1 ? p2 : p1, `hit-${projectile.damage % 3 + 1}`);
              if (struck.health === 0) window.setTimeout(() => resolveRound(projectile.owner), 80);
            }
          }
          projectiles = projectiles.filter((projectile) => !removedProjectiles.has(projectile.id));
          if (mode === '1p') {
            const distance = Math.abs(nextP1.x - nextP2.x);
            if (distance < 15 && Math.random() < 0.018 * aggression * combatSpeed) {
              const roll = Math.random();
              window.setTimeout(() => strike(2, roll < 0.28 ? 'uppercut' : roll < 0.5 ? 'kick' : 'punch'), 0);
            } else if (distance >= 15 && Math.random() < 0.0028 * aggression * combatSpeed) {
              window.setTimeout(() => strike(2, 'special'), 0);
            }
          }
          return { p1: nextP1, p2: nextP2, projectiles };
        });
      }
      animation = requestAnimationFrame(tick);
    };
    animation = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animation);
  }, [encounter, matchNumber, mode, p1, p2, playEffect, playFightVoice, registerComboHit, resolveRound, screen, showSettings, strike]);

  useEffect(() => {
    let previous = new Map<number, boolean>();
    const timer = window.setInterval(() => {
      Array.from(navigator.getGamepads?.() ?? []).forEach((pad, index) => {
        if (!pad || index > 1) return;
        const player = index === 0 ? 1 : 2;
        if (player === 2 && mode !== '2p') return;
        const pressed = new Map(pad.buttons.map((button, buttonIndex) => [buttonIndex, button.pressed]));
        if (pressed.get(9) && !previous.get(index * 20 + 9)) setShowSettings((current) => !current);
        if (showSettingsRef.current) {
          previous = new Map([...previous, ...Array.from(pressed, ([button, value]) => [index * 20 + button, value] as const)]);
          return;
        }
        const setPadKey = (name: string, active: boolean) => active ? keys.current.add(`Pad${player}${name}`) : keys.current.delete(`Pad${player}${name}`);
        const horizontal = pad.axes[0] ?? 0;
        const vertical = pad.axes[1] ?? 0;
        const leftActive = horizontal < -.35 || Boolean(pressed.get(14));
        const rightActive = horizontal > .35 || Boolean(pressed.get(15));
        const upActive = vertical < -.48 || Boolean(pressed.get(12));
        const downActive = vertical > .48 || Boolean(pressed.get(13));
        setPadKey('Left', leftActive);
        setPadKey('Right', rightActive);
        setPadKey('Up', upActive);
        setPadKey('Down', downActive);
        setPadKey('Block', Boolean(pressed.get(5)));
        setPadKey('Run', Boolean(pressed.get(4)));
        if (screen === 'fight' || screen === 'finisher') {
          if (downActive && !previous.get(index * 20 + 13)) recordDirection(player, 'down');
          if (leftActive && !previous.get(index * 20 + 14)) recordDirection(player, 'left');
          if (rightActive && !previous.get(index * 20 + 15)) recordDirection(player, 'right');
        }
        if (screen === 'select') {
          if ((horizontal < -.45 || pressed.get(14)) && !previous.get(index * 20 + 14)) setCursor((current) => (current - 1 + roster.length) % roster.length);
          if ((horizontal > .45 || pressed.get(15)) && !previous.get(index * 20 + 15)) setCursor((current) => (current + 1) % roster.length);
          if ((vertical < -.5 || pressed.get(12)) && !previous.get(index * 20 + 12)) setCursor((current) => (current - 5 + roster.length) % roster.length);
          if ((vertical > .5 || pressed.get(13)) && !previous.get(index * 20 + 13)) setCursor((current) => (current + 5) % roster.length);
          if (pressed.get(0) && !previous.get(index * 20)) choose(cursor);
        } else {
          if (pressed.get(2) && !previous.get(index * 20 + 2)) strike(player, consumeSpecialMotion(player) ? 'special' : 'punch');
          if (pressed.get(3) && !previous.get(index * 20 + 3)) strike(player, 'uppercut');
          if (pressed.get(0) && !previous.get(index * 20)) strike(player, 'kick');
          if (pressed.get(1) && !previous.get(index * 20 + 1)) strike(player, 'special');
        }
        pressed.set(14, Boolean(pressed.get(14) || horizontal < -.45));
        pressed.set(15, Boolean(pressed.get(15) || horizontal > .45));
        pressed.set(12, Boolean(pressed.get(12) || vertical < -.5));
        pressed.set(13, Boolean(pressed.get(13) || vertical > .5));
        previous = new Map([...previous, ...Array.from(pressed, ([button, value]) => [index * 20 + button, value] as const)]);
      });
    }, 34);
    return () => {
      window.clearInterval(timer);
      Array.from(keys.current).filter((key) => key.startsWith('Pad')).forEach((key) => keys.current.delete(key));
    };
  }, [choose, consumeSpecialMotion, cursor, mode, recordDirection, roster.length, screen, strike]);

  const nextMatch = () => {
    if (mode === '1p' && p1Index !== null) {
      const canAdvance = resultWinner === 1 || Boolean(encounter?.forcedLoss);
      if (!canAdvance) {
        retryMatch();
        return;
      }
      const nextIndex = campaignIndex + 1;
      if (nextIndex >= campaign.length) return;
      try { window.localStorage.setItem(campaignStorageKey(p1.id), String(nextIndex)); } catch { /* private browsing can reject persistence */ }
      startCampaignFight(p1Index, nextIndex);
      return;
    }
    setMatchNumber((current) => current + 1);
    setP2Index(null);
    setSelectionTurn(1);
    setCursor((p1Index ?? 0) + 1 < roster.length ? (p1Index ?? 0) + 1 : 0);
    setAnnouncement('CHOOSE YOUR NEXT RIVAL');
    setScreen('select');
  };

  const retryMatch = () => {
    setBattle(freshBattle());
    setRoundNumber(1);
    setRoundWins({ p1: 0, p2: 0 });
    clearCombo();
    roundResolving.current = false;
    setGibBurst(0);
    const roundCall = 'ROUND 1 // FIGHT!';
    setAnnouncement(roundCall);
    setScreen('fight');
    setResultWinner(1);
    window.setTimeout(() => setAnnouncement((current) => current === roundCall ? '' : current), 1500);
  };

  const newCampaign = () => {
    if (p1Index !== null) {
      try { window.localStorage.removeItem(campaignStorageKey(p1.id)); } catch { /* persistence is optional */ }
    }
    setP1Index(null);
    setP2Index(null);
    setRoundNumber(1);
    setRoundWins({ p1: 0, p2: 0 });
    clearCombo();
    setCampaignIndex(0);
    setCursor(0);
    setSelectionTurn(1);
    setAnnouncement('CHOOSE YOUR FIGHTER');
    setScreen('select');
  };

  if (!roster.length || !p1 || !p2 || !stage) return null;

  if (screen === 'select') {
    const previewCampaign = buildCampaign(roster, p1.id);
    const resumeAt = mode === '1p' ? savedCampaignIndex(p1.id, previewCampaign.length) : 0;
    return <section className="fight-select" aria-label="Choose a fighter">
    <header><button type="button" onClick={onExit}>EXIT TO ATTRACT</button><div><span>{mode === '1p' ? 'ONE PLAYER CAMPAIGN' : 'TWO PLAYER KOMBAT'}</span><h1>{announcement}</h1><b>30 FIGHTERS // {mode === '1p' ? `${previewCampaign.length} FIGHT LADDER` : 'LOCAL VERSUS'} // BEST OF 3</b></div><button type="button" onClick={() => setShowControls((current) => !current)}>CONTROLS</button></header>
    <div className="fight-select__preview fight-select__preview--left"><IdleFighterSprite character={p1} side="left" /><strong>P1 // {p1.name}{resumeAt > 0 ? ` // RESUME FIGHT ${resumeAt + 1}` : ''}</strong></div>
    <div className="fight-select__roster">{roster.map((character, index) => <button key={character.id} type="button" className={`${cursor === index ? 'is-current' : ''}${p1Index === index ? ' is-p1' : ''}`} onMouseEnter={() => setCursor(index)} onClick={() => choose(index)}><img src={character.images[0]?.src} alt="" /><span>{character.name}</span></button>)}</div>
    <div className="fight-select__preview fight-select__preview--right"><IdleFighterSprite character={p2} side="right" /><strong>{mode === '2p' ? 'P2' : 'CPU'} // {p2.name}</strong></div>
    {showControls && <aside className="fight-controls"><b>ARCADE CONTROLS</b><p>{fightControls.p1}</p>{mode === '2p' && <p>{fightControls.p2}</p>}<p>{fightControls.controller}</p></aside>}
  </section>;
  }

  const winner = resultWinner === 1 ? p1 : p2;
  const isBoss = encounter && encounter.kind !== 'roster';
  const encounterClass = encounter ? ` encounter-${encounter.kind}` : '';
  const riggedBout = encounter?.kind === 'cheater-rigged' ? Number(encounter.id.slice(-1)) : 0;
  const resultTitle = campaignComplete
    ? `${p1.name} BROKE THE ISLAND BRACKET`
    : resultWinner === 1 ? `${winner.name} LAID DOWN THE SMACK`
      : encounter?.forcedLoss ? `THE CHEATER STOLE BOUT ${riggedBout} OF 3`
        : `${p1.name} GOT SMACKED DOWN`;
  return <section className={`fighting-game fighting-game--${screen}${gibBurst ? ` has-finisher finisher-style-${finisherStyle} finisher-seed-${finisherSeed}` : ''}${isBoss ? ' is-boss-fight' : ''}${encounterClass}`} style={{ '--stage-image': `url("${stage.image}")`, '--stage-accent': stage.accent, '--stage-soft': stage.accentSoft, '--finisher-accent': winner.theme.accent } as CSSProperties}>
    <div className={`fight-stage fight-stage--${stage.ambience}`} aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>
    <header className="fight-hud">
      <div className="fight-hud__player"><span>{p1.name}<output className="fight-round-wins" aria-label={`${roundWins.p1} rounds won`}><em className={roundWins.p1 >= 1 ? 'is-won' : ''} /><em className={roundWins.p1 >= 2 ? 'is-won' : ''} /></output></span><div><i style={{ width: `${battle.p1.health}%` }} /></div><b>{Math.ceil(battle.p1.health)}</b></div>
      <div className="fight-hud__center"><strong>∞</strong><b>ROUND {roundNumber}</b><span>{mode === '1p' ? `FIGHT ${campaignIndex + 1}/${campaign.length}` : `MATCH ${matchNumber}`}</span></div>
      <div className="fight-hud__player fight-hud__player--right"><span><output className="fight-round-wins" aria-label={`${roundWins.p2} rounds won`}><em className={roundWins.p2 >= 1 ? 'is-won' : ''} /><em className={roundWins.p2 >= 2 ? 'is-won' : ''} /></output>{p2.name}</span><div><i style={{ width: `${battle.p2.health}%` }} /></div><b>{Math.ceil(battle.p2.health)}</b></div>
    </header>
    <div className={`fight-combatant fight-combatant--p1 fighter-archetype-${p1Combat.archetype}${battle.p1.y > 0 ? ' is-airborne' : ''}${battle.p1.hit ? ' is-hit' : ''}${battle.p1.blocking ? ' is-blocking' : ''}${battle.p1.crouching ? ' is-crouching' : ''}${battle.p1.attack ? ` is-${battle.p1.attack}` : ''}`} style={{ left: `${battle.p1.x}%`, bottom: `${8 + battle.p1.y}%`, '--fighter-accent': p1.theme.accent } as CSSProperties}><IdleFighterSprite character={p1} side="left" />{screen === 'finisher' && resultWinner === 2 && <span className="fight-stars">✦ ✦ ✦</span>}</div>
    <div className={`fight-combatant fight-combatant--p2 fighter-archetype-${p2Combat.archetype}${battle.p2.y > 0 ? ' is-airborne' : ''}${battle.p2.hit ? ' is-hit' : ''}${battle.p2.blocking ? ' is-blocking' : ''}${battle.p2.crouching ? ' is-crouching' : ''}${battle.p2.attack ? ` is-${battle.p2.attack}` : ''}${encounter?.kind === 'dark-self' ? ' is-dark-copy' : ''}`} style={{ left: `${battle.p2.x}%`, bottom: `${8 + battle.p2.y}%`, '--fighter-accent': p2.theme.accent } as CSSProperties}><IdleFighterSprite character={p2} side="right" />{screen === 'finisher' && resultWinner === 1 && <span className="fight-stars">✦ ✦ ✦</span>}</div>
    {battle.projectiles.map((projectile) => {
      const projectileCombat = projectile.owner === 1 ? p1Combat : p2Combat;
      return <i key={projectile.id} className={`fight-projectile fight-projectile--p${projectile.owner} fight-projectile--${projectileCombat.specialShape}`} style={{ left: `${projectile.x}%`, '--fighter-accent': projectile.owner === 1 ? p1.theme.accent : p2.theme.accent, '--projectile-scale': projectile.scale, '--projectile-direction': projectile.direction } as CSSProperties} aria-hidden="true" />;
    })}
    {combo && <div className={`fight-combo fight-combo--p${combo.owner}`}><strong>{combo.count} HIT</strong><span>SMACK CHAIN</span></div>}
    {announcement && <div className="fight-announcement">{announcement}</div>}
    {screen === 'finisher' && <div className="fight-finisher-help">{resultWinner === 1 ? 'P1' : 'P2'} GET CLOSE // HOLD DOWN + UPPERCUT</div>}
    {vandalVisible && <aside className="fight-vandal-toast"><div><strong>SMACK CONFIRMED!</strong><span>VANDAL SAW THAT.</span></div><img src={VANDAL_GIF} alt="Vandal signals from the corner" /></aside>}
    {gibBurst > 0 && <div className="fight-gibs" aria-hidden="true">{Array.from({ length: gibCount }, (_, index) => <i key={`${gibBurst}-${index}`} style={{ '--gib': index } as CSSProperties} />)}</div>}
    {screen === 'fight' && <div className={`fight-special-guide${mode === '2p' ? ' fight-special-guide--2p' : ''}`}><span><b>P1 {p1Combat.specialShape}</b> DOWN → TOWARD + PUNCH <i>Q</i></span>{mode === '2p' && <span><b>P2 {p2Combat.specialShape}</b> DOWN → TOWARD + PUNCH <i>O</i></span>}<em>PAD: DOWN → TOWARD + X <i>B</i></em></div>}
    <p className="fight-stage-name"><b>{stage.pit ? 'DANGER // PIT STAGE' : 'STAGE'} — {stage.name}</b><span>{encounter?.title ?? stage.motif}</span></p>
    <button className="fight-menu-button" type="button" onClick={() => setShowSettings(true)}>MENU</button>
    {screen === 'result' && <div className="fight-result">
      <span>{campaignComplete ? 'CAMPAIGN COMPLETE // ISLAND ZERO CLEARED' : resultWinner === 1 ? 'VICTORY' : encounter?.forcedLoss ? 'COUNTERFEIT RESULT' : 'DEFEAT'}</span>
      <h1>{resultTitle}</h1>
      {mode === '1p' && <p>{encounter?.title}<b>{campaignIndex + 1} / {campaign.length}</b></p>}
      {campaignComplete ? <>
        <button type="button" onClick={onPlayShooter}>PLAY SHOOTER</button>
        <button type="button" onClick={newCampaign}>NEW FIGHTING CAMPAIGN</button>
      </> : mode === '1p' ? <>
        {(resultWinner === 1 || encounter?.forcedLoss) && <button type="button" onClick={nextMatch}>{encounter?.forcedLoss ? riggedBout === 3 ? 'DEMAND A FAIR FIGHT' : 'GET CHEATED AGAIN' : 'CONTINUE CAMPAIGN'}</button>}
        <button type="button" onClick={retryMatch}>{encounter?.forcedLoss ? 'RETRY THIS RIGGED BOUT' : 'RETRY FIGHTING GAME'}</button>
        <button type="button" onClick={onPlayShooter}>PLAY SHOOTER</button>
      </> : <>
        <button type="button" onClick={nextMatch}>NEXT MATCH</button>
        <button type="button" onClick={retryMatch}>RETRY FIGHTING GAME</button>
        <button type="button" onClick={onPlayShooter}>PLAY SHOOTER</button>
      </>}
      <button type="button" onClick={onExit}>RETURN TO ATTRACT</button>
    </div>}
    {showSettings && <div className="fight-settings"><div><header><h2>PAUSE / SETTINGS</h2><button type="button" onClick={() => setShowSettings(false)}>RESUME</button></header><label>FIGHTER VOICES <b>{Math.round(audioPreferences.fightVoiceVolume * 100)}%</b><input type="range" min="0" max="1.5" step="0.05" value={audioPreferences.fightVoiceVolume} onChange={(event) => updateAudioPreferences({ fightVoiceVolume: Number(event.target.value) })} /></label><button type="button" onClick={() => updateAudioPreferences({ fightVoiceSpeed: nextVoiceSpeed(audioPreferences.fightVoiceSpeed) })}>VOICE SPEED <b>{audioPreferences.fightVoiceSpeed}×</b> <small>Clips baked at Kokoro 1.2</small></button><label>MUSIC <b>{Math.round(audioPreferences.fightMusicVolume * 100)}%</b><input type="range" min="0" max="1" step="0.05" value={audioPreferences.fightMusicVolume} onChange={(event) => updateAudioPreferences({ fightMusicVolume: Number(event.target.value) })} /></label><label>EFFECTS <b>{Math.round(audioPreferences.fightEffectsVolume * 100)}%</b><input type="range" min="0" max="1.5" step="0.05" value={audioPreferences.fightEffectsVolume} onChange={(event) => updateAudioPreferences({ fightEffectsVolume: Number(event.target.value) })} /></label><p>{fightControls.p1}</p><p>{fightControls.controller}</p><button type="button" onClick={onExit}>EXIT TO ATTRACT</button></div></div>}
  </section>;
}
